/*
  Stone necklace "Swap sheet" (product.stone-necklace template). See snippets/stone-swap-sheet.liquid.

  Dawn stays in charge of the product: this moves Dawn's variant-selects, price, quantity and
  product form into the sheet (all still inside product-info, so Dawn's own updates keep working)
  and reacts to its native radio changes. It never sets the variant itself.

  Phone dock (< 750px). stone-swap[data-d] is the one source of truth for its state:
  - 'peek'  partial: name, price, Stone / Chain tabs, buy row. Shown at the top of the page.
  - 'half'  expanded: the picker panel. Opened by the shopper only; only the shopper closes it.
  - 'mini'  a slim summary bar, once the shopper has scrolled down the page.
  setDock() is the only place that writes it. The sheet moves between states with transforms only
  (stone-swap.css), so a state change never shifts the page's layout.
*/
const SWAP_DOCK = {
  // A real scroll this far down from where the dock last changed state turns 'peek' into 'mini'.
  SCROLL_DOWN_PX: 32,
  // scrollY at or under this counts as "at the top" (covers sub-pixel and rubber-band jitter).
  TOP_PX: 4,
  // After a finger leaves the dock, wait this long before any automatic change, so a tap's click
  // still lands on what the finger went down on.
  TAP_GRACE_MS: 150,
  // The longest dock animation (stone-swap.css) plus a margin: after this, data-rest follows
  // data-d and what the state hides leaves the focus order (one style change, after the motion).
  SETTLE_MS: 320,
  // A change event this soon after a pointerdown on an option is a tap (not arrow-key browsing).
  PICK_WINDOW_MS: 1500,
  // Programmatic scrolls: frames without movement that count as "stopped", and a hard limit.
  STILL_FRAMES: 4,
  STALL_FRAMES: 10,
  MAX_FRAMES: 180,
  // Name sizes in the tabs and the mini bar step down to fit one line, never below this.
  MIN_FONT_PX: 12,
  // A drag on the sheet's handle further than this opens or closes it; less is a tap.
  DRAG_PX: 24,
};

if (!customElements.get('stone-swap')) {
  customElements.define(
    'stone-swap',
    class StoneSwap extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.productInfo = this.closest('product-info');
        this.page = this.productInfo?.querySelector('.product');
        this.area = document.getElementById(`SwapStage-${this.section}`);
        this.stage = this.area?.querySelector('.swap-stage');
        this.viewer = this.area?.querySelector('[data-swap-viewer]');
        this.wrap = this.area?.querySelector('.swap-stage-wrap');
        this.live = document.getElementById(`SwapLive-${this.section}`);
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.activePart = null;
        this.dockAnchor = window.scrollY;

        this.adoptDawnParts();
        this.keepLastWordsTogether();
        this.bindEvents();
        this.initViewer();
        this.initMini();
        this.previous = this.selection();
        this.render({ announce: false });
        // Viewport checks (keyboard lift only) start once the page has loaded and settled.
        const settled = () => setTimeout(() => (this.loaded = true), SWAP_DOCK.SETTLE_MS);
        if (document.readyState === 'complete') settled();
        else window.addEventListener('load', settled, { once: true });
        // Opened already scrolled down (a restored scroll position): start as the slim bar.
        if (this.mobile.matches && !this.atTop()) this.dataset.d = this.dataset.rest = 'mini';
        this.updateDock();
        this.sync();
        this.classList.add('is-ready');
        this.page?.classList.add('swap-ready');
      }

      /* ---------- Dawn parts ---------- */

      adoptDawnParts() {
        const slot = (name) => this.querySelector(`[data-swap-slot="${name}"]`);
        const variantSelects = this.productInfo.querySelector('variant-selects');
        if (variantSelects) slot('picker').appendChild(variantSelects);
        // Dawn's title block (the page's h1) leads the sheet: product name, then price.
        const title = this.productInfo.querySelector('.product__title');
        if (title) slot('title').appendChild(title);
        const price = this.productInfo.querySelector(`#price-${this.section}`);
        if (price) {
          slot('price').querySelector('.swap-sheet__price-ph')?.remove();
          slot('price').appendChild(price);
        }
        const quantity = this.productInfo.querySelector(`#Quantity-Form-${this.section}`);
        if (quantity) slot('buy').appendChild(quantity);
        const form = this.productInfo.querySelector('product-form');
        if (form) slot('buy').appendChild(form);
      }

      // No paragraph ends on a lone word: join each one's last two words with a no-break space (the
      // text is unchanged; only where it may break). text-wrap: pretty in the CSS doesn't catch
      // every case and isn't in every browser.
      keepLastWordsTogether() {
        // Text blocks under the buy panel that list phrases between ✨ marks: keep each phrase
        // (with its mark) whole, so lines only break between phrases.
        this.productInfo?.querySelectorAll('.product__text').forEach((block) => {
          if (block.children.length || !block.textContent.includes('✨')) return;
          const parts = block.textContent.trim().split(/\s*(?=✨)/).filter(Boolean);
          block.replaceChildren(
            ...parts.flatMap((part, i) => [
              ...(i ? [' '] : []),
              Object.assign(document.createElement('span'), { className: 'swap-nowrap', textContent: part.trim() }),
            ])
          );
        });
        const details = document.getElementById(`SwapDetails-${this.section}`);
        // Paragraphs holding only a non-breaking space (spacers pasted into the description) would
        // add a blank line; hide them (stone-swap.css). Runs before the details row is shown.
        details?.querySelectorAll('.product__description p').forEach((p) => {
          if (!p.children.length && !p.textContent.replace(/\u00a0/g, ' ').trim()) p.classList.add('swap-empty');
        });
        [this, details].forEach((root) =>
          root?.querySelectorAll('p, li').forEach((block) => {
            const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
            let last = null;
            for (let node = walker.nextNode(); node; node = walker.nextNode()) if (node.textContent.trim()) last = node;
            if (!last) return;
            const text = last.textContent.replace(/\s+$/, '');
            const i = text.lastIndexOf(' ');
            // Only within one text node, and not when the pair would be too long for a line.
            if (i > 0 && text.length - i < 24) last.textContent = `${text.slice(0, i)} ${text.slice(i + 1)}${last.textContent.slice(text.length)}`;
          })
        );
      }

      // Dawn swaps in a new variant-selects and hides the old one for 500ms; read the visible one.
      selection() {
        const visible = [...this.querySelectorAll('variant-selects')].find((el) => el.style.display !== 'none');
        const values = {};
        visible?.querySelectorAll('fieldset').forEach((fieldset) => {
          const input = fieldset.querySelector('input:checked');
          if (input) values[input.name.trim().replace(/-\d+$/, '')] = input.value;
        });
        return { stone: values.Stone, chain: values.Chain, length: values.Length };
      }

      /* ---------- Rendering ---------- */

      render({ announce = true } = {}) {
        const { stone, chain, length } = this.selection();
        if (!stone || !chain) return;
        const stoneBlock = this.querySelector(`.swap-live__stone[data-stone="${CSS.escape(stone)}"]`);
        const gem = stoneBlock?.dataset.gem;

        this.querySelectorAll('[data-swap-bind="summary"]').forEach((el) => (el.textContent = `${chain} · ${stone}`));
        this.querySelectorAll('[data-swap-bind="stone"]').forEach((el) => (el.textContent = stone));
        this.querySelectorAll('[data-swap-bind="chain"]').forEach((el) => (el.textContent = chain));
        // The tab is narrow: "20 inch" reads as 20″ there (the picker and cart keep "20 inch").
        this.querySelectorAll('[data-swap-bind="length"]').forEach((el) => (el.textContent = (length || '').replace(' inch', '″')));
        this.querySelectorAll('[data-swap-bind="words"]').forEach((el) => (el.textContent = stoneBlock?.dataset.words || ''));
        // The mini bar: "Gold Copper · 18″ · $80" under the stone name.
        const price = this.priceText();
        const shortLength = (length || '').replace(' inch', '″');
        const meta = [chain, shortLength, price].filter(Boolean).join(' · ');
        this.querySelectorAll('[data-swap-bind="mini-meta"]').forEach((el) => (el.textContent = meta));
        this.querySelector('[data-swap-expand]')?.setAttribute(
          'aria-label',
          `Swap stone or chain. Now: ${[stone, chain, length, price].filter(Boolean).join(', ')}`
        );
        this.querySelectorAll('[data-swap-bind="gem"]').forEach((el) => gem && (el.style.backgroundImage = `url("${gem}")`));
        // Chain cards preview the real combination: each chain's render with the selected stone.
        const renders = this.renderMap()[stone] || {};
        // On the picker, not the sheet: custom properties declared on the (animated) sheet would
        // restyle all of it every animation frame (stone-swap.css).
        const picker = this.querySelector('[data-swap-slot="picker"]');
        this.querySelectorAll('[data-swap-bind="chain-thumb"]').forEach((el) => renders[chain] && (el.src = renders[chain]));
        ['Pure Copper', 'Gold Copper'].forEach((name) => {
          if (renders[name] && picker) picker.style.setProperty(`--chain-thumb-${name === 'Gold Copper' ? 'gold' : 'pure'}`, `url("${renders[name]}")`);
        });
        this.querySelectorAll('[data-stone]').forEach((el) => (el.hidden = el.dataset.stone !== stone));
        // Dawn's Chain and Length legends only name the option; echo the choice like the Stone legend.
        this.querySelectorAll('variant-selects .product-form__input--pill').forEach((fieldset) => {
          const legend = fieldset.querySelector('legend');
          const value = fieldset.querySelector('input:checked')?.value || '';
          if (legend && !legend.querySelector('b')) legend.append(': ', Object.assign(document.createElement('b'), { textContent: value }));
          else if (legend) legend.querySelector('b').textContent = value;
        });

        this.fitTabs();
        this.measurePeek();

        const changed = this.previous && ['stone', 'chain', 'length'].some((k) => this.previous[k] !== { stone, chain, length }[k]);
        if (announce && changed) {
          this.live.textContent = `Now showing: ${chain}, ${stone}${length ? `, ${length}` : ''}`;
        }
        this.previous = { stone, chain, length };
      }

      // The price Dawn shows (sale price when on sale), without a ".00": "$80".
      priceText() {
        const box = this.querySelector('[data-swap-slot="price"] .price');
        const item = box?.classList.contains('price--on-sale')
          ? box.querySelector('.price__sale .price-item--last')
          : box?.querySelector('.price__regular .price-item--regular');
        return (item?.textContent || '').trim().replace(/[.,]00(?=\D*$)/, '');
      }

      // Tab names and the mini bar's lines stay on one line: if a long name ("Leopard Skin Jasper"
      // on a 360px phone) doesn't fit, its size steps down, never below SWAP_DOCK.MIN_FONT_PX. All
      // reads happen together, then all writes (text width scales with font size), then one check.
      fitTabs() {
        const els = [...this.querySelectorAll('.swap-tab__text b, .swap-mini__text b, .swap-mini__text small')];
        els.forEach((el) => (el.style.fontSize = ''));
        const fit = (measure) =>
          measure
            .map((el) => {
              const size = parseFloat(getComputedStyle(el).fontSize);
              const over = el.scrollWidth > el.clientWidth + 0.5 && el.clientWidth > 0;
              return over && [el, size, el.clientWidth / el.scrollWidth];
            })
            .filter(Boolean);
        const first = fit(els);
        first.forEach(([el, size, ratio]) => {
          el.style.fontSize = `${Math.max(SWAP_DOCK.MIN_FONT_PX, Math.floor(size * ratio * 2) / 2)}px`;
        });
        // Rounding can leave a hair too wide: one more half-pixel step where needed.
        fit(first.map(([el]) => el)).forEach(([el, size]) => {
          el.style.fontSize = `${Math.max(SWAP_DOCK.MIN_FONT_PX, size - 0.5)}px`;
        });
      }

      // Every stone + chain combination's render (from the sheet), so a picked option's image can
      // start loading at once instead of after Dawn's variant fetch.
      renderMap() {
        return (this.renders ||= JSON.parse(this.querySelector('[data-swap-renders]')?.textContent || '{}'));
      }

      renderFor({ stone, chain } = {}) {
        return this.renderMap()[stone]?.[chain];
      }

      // A render's file name identifies it whatever the host or width in its URL.
      renderKey(src) {
        return (src || '').split('?')[0].split('/').pop();
      }

      stageSrcset(src) {
        return [360, 540, 720, 900, 1080, 1440, 2048].map((w) => `${this.sized(src, w)} ${w}w`).join(', ');
      }

      stageSizes() {
        return this.stage?.querySelector('.swap-stage__img')?.sizes || '(min-width: 750px) 45vw, 90vw';
      }

      // Warm the cache for an option the shopper is about to pick (hover, touch, focus).
      preload(src) {
        const key = this.renderKey(src);
        if (!key || (this.preloaded ||= new Set()).has(key)) return;
        this.preloaded.add(key);
        const img = new Image();
        img.sizes = this.stageSizes();
        img.srcset = this.stageSrcset(src);
      }

      // Show a render on the stage. From the moment a new one is asked for, the current image softens
      // and a loader shows (.is-switching), so a slow connection never just shows the old necklace;
      // the new image is decoded off-screen, then dissolves in inside the fixed frame (nothing
      // moves). With reduced motion it simply replaces it. Asking again for the render already
      // showing or loading only fills in its media id and alt text.
      showRender(src, { id, alt } = {}) {
        if (!this.stage || !src) return;
        const key = this.renderKey(src);
        const current = this.stage.querySelector('.swap-stage__img.is-active');
        const known =
          this.pending?.key === key ? this.pending.img : !this.pending && this.renderKey(current?.src) === key ? current : null;
        if (known) {
          if (id) known.dataset.mediaId = id;
          if (alt) known.alt = alt;
          return;
        }
        const img = new Image();
        img.className = 'swap-stage__img';
        if (id) img.dataset.mediaId = id;
        img.alt = alt || '';
        img.sizes = this.stageSizes();
        img.srcset = this.stageSrcset(src);
        img.src = this.sized(src, 1080);
        const pending = (this.pending = { key, img });
        this.setSwitching(true);
        const show = () => {
          if (this.pending !== pending) return;
          this.pending = null;
          const old = this.stage.querySelector('.swap-stage__img.is-active');
          this.stage.appendChild(img);
          requestAnimationFrame(() => {
            img.classList.add('is-active');
            old?.classList.remove('is-active');
            this.setSwitching(false);
            const done = () => {
              this.stage.querySelectorAll('.swap-stage__img:not(.is-active)').forEach((el) => el.remove());
              this.refreshThumbs?.();
            };
            if (this.reducedMotion.matches) done();
            else setTimeout(done, 450);
          });
        };
        (img.decode ? img.decode() : Promise.resolve()).then(show, show);
      }

      // Dawn's variant update: the variant's own render (usually already showing or loading).
      swapImage(media) {
        const src = media?.preview_image?.src;
        if (src) this.showRender(src, { id: String(media.id), alt: media.alt });
      }

      setSwitching(on) {
        this.area?.classList.toggle('is-switching', on);
        this.viewer?.setAttribute('aria-busy', String(on));
      }

      // The shopper just picked an option: show its render right away.
      showSelection() {
        const { stone, chain } = this.selection();
        const src = this.renderFor({ stone, chain });
        if (!src) return;
        this.showRender(src, { alt: `${chain} classic handmade chain necklace with a wire-wrapped ${stone} stone pendant` });
        const closeup = this.viewer?.querySelector('[data-role="closeup"]');
        if (closeup && this.renderKey(closeup.src) !== this.renderKey(src)) {
          this.setSlide(closeup, { src });
          closeup.alt = `Close-up of the wire-wrapped ${stone} pendant`;
        }
      }

      // Hovering, touching or focusing an option that would change the image preloads that render.
      preloadIntent(event) {
        const target = event.target.closest?.('variant-selects label, variant-selects input');
        if (!target) return;
        const input = target.tagName === 'INPUT' ? target : document.getElementById(target.htmlFor);
        if (!input || input.checked) return;
        const option = input.name.trim().replace(/-\d+$/, '');
        if (option !== 'Stone' && option !== 'Chain') return;
        const selection = this.selection();
        selection[option.toLowerCase()] = input.value;
        this.preload(this.renderFor(selection));
        // The Stone tab's gem picture, too.
        const gem = option === 'Stone' && this.querySelector(`.swap-live__stone[data-stone="${CSS.escape(input.value)}"]`)?.dataset.gem;
        if (gem && !(this.preloaded ||= new Set()).has(gem)) {
          this.preloaded.add(gem);
          new Image().src = gem;
        }
      }


      /* ---------- Product viewer: slides, dots/thumbnails, full-screen zoom ---------- */

      initViewer() {
        if (!this.viewer) return;
        this.slides = [...this.viewer.querySelectorAll('.swap-slide')];
        this.index = 0;
        let ticking = false;
        this.viewer.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              ticking = false;
              const i = Math.round(this.viewer.scrollLeft / this.viewer.clientWidth);
              if (i !== this.index) this.setIndex(i);
            });
          },
          { passive: true }
        );
        // A plain tap on the stage while the phone sheet is open collapses it (like dragging it
        // down). A tap that moved more than ~10px (a scroll or drag) or used two fingers doesn't count.
        const pointers = new Map();
        this.tapMoved = false;
        this.area.addEventListener('pointerdown', (event) => {
          if (!pointers.size) this.tapMoved = false;
          pointers.set(event.pointerId, [event.clientX, event.clientY]);
          if (pointers.size > 1) this.tapMoved = true;
        });
        this.area.addEventListener('pointermove', (event) => {
          const start = pointers.get(event.pointerId);
          if (start && Math.hypot(event.clientX - start[0], event.clientY - start[1]) > 10) this.tapMoved = true;
        });
        ['pointerup', 'pointercancel'].forEach((type) =>
          this.area.addEventListener(type, (event) => {
            if (type === 'pointercancel') this.tapMoved = true;
            pointers.delete(event.pointerId);
          })
        );
        this.area.addEventListener('click', (event) => {
          if (this.sheetOpenOnPhone()) {
            if (!this.tapMoved && !event.target.closest('[data-swap-open]')) this.setDock(this.restingState());
            return;
          }
          const go = event.target.closest('[data-go]');
          if (go) return this.goTo(Number(go.dataset.go));
          if (event.target.closest('[data-swap-zoom]') || event.target.closest('.swap-slide img')) this.openLightbox(this.index);
        });
        this.viewer.addEventListener('keydown', (event) => {
          if (this.sheetOpenOnPhone()) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.collapse();
            }
            return;
          }
          if (event.key === 'ArrowRight') this.goTo(Math.min(this.index + 1, this.slides.length - 1));
          if (event.key === 'ArrowLeft') this.goTo(Math.max(this.index - 1, 0));
          if (event.key === 'Enter') this.openLightbox(this.index);
        });
        this.initLightbox();
        this.refreshThumbs();
      }

      setIndex(i) {
        this.index = i;
        this.area.querySelectorAll('[data-go]').forEach((el) => {
          const on = Number(el.dataset.go) === i;
          el.classList.toggle('is-active', on);
          on ? el.setAttribute('aria-current', 'true') : el.removeAttribute('aria-current');
        });
      }

      goTo(i, smooth = true) {
        if (!this.viewer) return;
        const behavior = smooth && !this.reducedMotion.matches ? 'smooth' : 'auto';
        this.viewer.scrollTo({ left: i * this.viewer.clientWidth, behavior });
        this.setIndex(i);
      }

      slideImage(i) {
        const slide = this.slides?.[i];
        return slide?.querySelector('.swap-stage__img.is-active') || slide?.querySelector('img');
      }

      refreshThumbs() {
        this.area?.querySelectorAll('.swap-thumbs__item img').forEach((thumb, i) => {
          const img = this.slideImage(i);
          const src = img && (img.currentSrc || img.src);
          // 64px tiles at 1x-3x; the close-up tile is scaled 3.1x, so it gets 3.1x the pixels.
          const t = i === 1 ? 200 : 64;
          if (src) {
            thumb.srcset = [1, 2, 3].map((x) => `${this.sized(src, t * x)} ${x}x`).join(', ');
            thumb.src = this.sized(src, t * 2);
          }
          thumb.parentElement.classList.toggle('swap-thumbs__item--closeup', i === 1);
        });
      }

      // A Shopify CDN URL at a given width.
      sized(src, width) {
        const [base, query = ''] = src.split('?');
        const params = query.split('&').filter((p) => p && !p.startsWith('width='));
        params.push(`width=${width}`);
        return `${base}?${params.join('&')}`;
      }

      setSlide(img, media) {
        const src = media?.preview_image?.src || media?.src;
        if (!img || !src) return;
        const widths = [360, 540, 720, 1080, 1440, 2048];
        img.srcset = widths.map((w) => `${this.sized(src, w)} ${w}w`).join(', ');
        img.src = this.sized(src, 1080);
        if (media.alt && img.dataset.role !== 'closeup') img.alt = media.alt;
      }

      // Keep the close-up slide on the selected variant's render.
      updateSlides(variant) {
        if (!this.viewer || !variant) return;
        const closeup = this.viewer.querySelector('[data-role="closeup"]');
        this.setSlide(closeup, variant.featured_media);
        if (closeup) closeup.alt = `Close-up of the wire-wrapped ${variant.option1} pendant`;
        this.refreshThumbs();
      }

      initLightbox() {
        const box = (this.lightbox = this.area.querySelector('[data-swap-lightbox]'));
        if (!box) return;
        const frame = box.querySelector('[data-lightbox-frame]');
        const img = box.querySelector('[data-lightbox-img]');
        const z = (this.zoomState = { k: 1, x: 0, y: 0 });
        const apply = () => {
          img.style.transform = `translate(${z.x}px, ${z.y}px) scale(${z.k})`;
          box.classList.toggle('is-zoomed', z.k > 1);
        };
        const clamp = () => {
          z.k = Math.min(5, Math.max(1, z.k));
          const r = frame.getBoundingClientRect();
          const maxX = ((z.k - 1) * r.width) / 2;
          const maxY = ((z.k - 1) * r.height) / 2;
          z.x = Math.min(maxX, Math.max(-maxX, z.x));
          z.y = Math.min(maxY, Math.max(-maxY, z.y));
          if (z.k === 1) z.x = z.y = 0;
        };
        // Zoom by `factor` keeping the point (cx, cy) under the finger / cursor.
        const zoomAt = (factor, cx, cy) => {
          const r = frame.getBoundingClientRect();
          const px = cx - (r.left + r.width / 2);
          const py = cy - (r.top + r.height / 2);
          const k = Math.min(5, Math.max(1, z.k * factor));
          z.x = px - ((px - z.x) * k) / z.k;
          z.y = py - ((py - z.y) * k) / z.k;
          z.k = k;
          clamp();
          apply();
        };
        this.resetZoom = () => {
          z.k = 1;
          z.x = z.y = 0;
          apply();
        };

        const pointers = new Map();
        let pinch = null;
        let lastTap = 0;
        let swipeStart = null;
        frame.addEventListener('pointerdown', (e) => {
          frame.setPointerCapture(e.pointerId);
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pointers.size === 2) {
            const [a, b] = [...pointers.values()];
            pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), k: z.k };
          }
          swipeStart = pointers.size === 1 ? { x: e.clientX, y: e.clientY } : null;
        });
        frame.addEventListener('pointermove', (e) => {
          if (!pointers.has(e.pointerId)) return;
          const prev = pointers.get(e.pointerId);
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pointers.size === 2 && pinch) {
            const [a, b] = [...pointers.values()];
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            zoomAt((pinch.k * (d / pinch.d)) / z.k, (a.x + b.x) / 2, (a.y + b.y) / 2);
          } else if (pointers.size === 1 && z.k > 1) {
            z.x += e.clientX - prev.x;
            z.y += e.clientY - prev.y;
            clamp();
            apply();
          }
        });
        const end = (e) => {
          pointers.delete(e.pointerId);
          if (pointers.size < 2) pinch = null;
          if (e.type !== 'pointerup' || pointers.size) return;
          const now = Date.now();
          if (swipeStart && z.k === 1) {
            const dx = e.clientX - swipeStart.x;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(e.clientY - swipeStart.y)) {
              this.stepLightbox(dx < 0 ? 1 : -1);
              lastTap = 0;
              return;
            }
          }
          if (now - lastTap < 300) {
            z.k > 1 ? this.resetZoom() : zoomAt(2.5, e.clientX, e.clientY);
            lastTap = 0;
          } else {
            lastTap = now;
          }
        };
        frame.addEventListener('pointerup', end);
        frame.addEventListener('pointercancel', end);
        frame.addEventListener(
          'wheel',
          (e) => {
            e.preventDefault();
            zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
          },
          { passive: false }
        );
        box.addEventListener('click', (e) => {
          if (e.target.closest('[data-lightbox-close]') || e.target === box) box.close();
          const step = e.target.closest('[data-lightbox-step]');
          if (step) this.stepLightbox(Number(step.dataset.lightboxStep));
        });
        box.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.stepLightbox(1);
          if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        });
        box.addEventListener('close', () => this.goTo(this.lightboxIndex ?? this.index, false));
      }

      openLightbox(i) {
        if (!this.lightbox) return;
        this.lightboxIndex = i;
        this.showLightboxImage();
        if (!this.lightbox.open) this.lightbox.showModal();
      }

      stepLightbox(step) {
        const n = this.slides.length;
        this.lightboxIndex = (this.lightboxIndex + step + n) % n;
        this.showLightboxImage();
      }

      showLightboxImage() {
        const source = this.slideImage(this.lightboxIndex);
        const img = this.lightbox.querySelector('[data-lightbox-img]');
        const src = source && (source.currentSrc || source.src);
        if (!src) return;
        this.resetZoom();
        img.src = this.sized(src, 2048);
        img.alt = source.alt;
        img.classList.toggle('is-closeup', source.dataset.role === 'closeup');
        this.lightbox.querySelector('[data-lightbox-count]').textContent = `${this.lightboxIndex + 1} / ${this.slides.length}`;
      }

      /* ---------- Sheet / phone dock ---------- */

      atTop() {
        return window.scrollY <= SWAP_DOCK.TOP_PX;
      }

      // Where the phone dock rests while the picker is closed: partial at the top, the slim bar below.
      restingState() {
        return this.atTop() ? 'peek' : 'mini';
      }

      // The only writer of data-d. Desktop always stays 'peek' (its panel has no states).
      setDock(d) {
        if (!this.mobile.matches && d === 'mini') d = 'peek';
        if (d !== 'half' && this.mobile.matches) this.activePart = null;
        this.dockAnchor = window.scrollY;
        if (this.dataset.d !== d) {
          this.dataset.d = d;
          clearTimeout(this.restTimer);
          this.restTimer = setTimeout(() => {
            this.dataset.rest = this.dataset.d;
            // Re-check the viewport after every state change too (once the move is over), so a
            // measurement can never stay stale for a whole visit.
            this.measureViewport();
          }, SWAP_DOCK.SETTLE_MS);
        }
        this.sync();
      }

      // Older name, kept for anything still calling it.
      setHeight(d) {
        this.setDock(d);
      }

      open(part) {
        this.lastPart = part;
        if (!this.mobile.matches) {
          this.activePart = part;
          this.sync();
          return;
        }
        if (this.activePart === part && this.dataset.d === 'half') {
          this.setDock(this.restingState());
          return;
        }
        this.activePart = part;
        this.setDock('half');
        this.querySelector('.swap-sheet__panel').scrollTop = 0;
      }

      // The slim bar was tapped: open the picker on the part last used, focus its tab.
      expand() {
        this.open(this.lastPart || 'stone');
        this.querySelector(`.swap-tab[data-swap-open="${this.activePart}"]`)?.focus({ preventScroll: true });
      }

      sheetOpenOnPhone() {
        return this.mobile.matches && this.dataset.d === 'half';
      }

      // Close the picker (to `to`, by default wherever the dock rests here) and put focus on the
      // control that reopens it: the tab that opened it, or the slim bar's Swap button.
      collapse(to = this.restingState()) {
        const opener = this.querySelector(`.swap-tab[data-swap-open="${this.activePart || 'stone'}"]`);
        this.setDock(to);
        if (to === 'mini') this.querySelector('[data-swap-expand]')?.focus({ preventScroll: true });
        else opener?.focus({ preventScroll: true });
      }

      // Scroll-driven dock changes, called at most once a frame. Nothing changes during our own
      // scrolls, while Dawn's cart notification shows, or while (or just after) a finger is on the
      // dock. Partial <-> slim bar by position; the open picker closes once the page itself is
      // scrolled (the picker's own list scrolls inside it and never moves the page).
      updateDock() {
        // The page has left its top once: show what was kept unpainted under the dock (stone-swap.css).
        if (!this.scrolledOnce && window.scrollY > SWAP_DOCK.TOP_PX) {
          this.scrolledOnce = true;
          this.page?.classList.add('swap-scrolled');
        }
        if (!this.mobile.matches || this.autoScroll || this.beforeNotice) return;
        if (this.fingerDown || performance.now() - (this.fingerUpAt ?? -Infinity) < SWAP_DOCK.TAP_GRACE_MS) return;
        const y = window.scrollY;
        const d = this.dataset.d;
        // Content loading above the fold moves scrollY too (scroll anchoring) without anyone
        // scrolling: when the page's height changed, start measuring again from here.
        // Scroll anchoring moves scrollY by exactly as much as the page grew (pageGrowth, from a
        // ResizeObserver, so nothing here reads layout); move the anchor with it.
        const grew = this.pageGrowth || 0;
        const moved = this.lastY === undefined ? 0 : y - this.lastY;
        this.pageGrowth = 0;
        this.lastY = y;
        if (grew && moved && Math.abs(moved - grew) <= 1) {
          this.dockAnchor += moved;
          return;
        }
        if (d === 'half') {
          if (Math.abs(y - this.dockAnchor) <= SWAP_DOCK.SCROLL_DOWN_PX) return;
          const panel = this.querySelector('.swap-sheet__panel');
          const focusInPanel = !!panel?.contains(document.activeElement);
          const to = this.restingState();
          this.setDock(to);
          // Focus only moves if it was in the panel that just closed.
          if (focusInPanel) {
            const target = to === 'mini' ? this.querySelector('[data-swap-expand]') : this.querySelector('.swap-tab[data-swap-open="stone"]');
            target?.focus({ preventScroll: true });
          }
        } else if (y <= SWAP_DOCK.TOP_PX) {
          if (d !== 'peek') this.setDock('peek');
          this.dockAnchor = y;
        } else if (d === 'peek') {
          if (y - this.dockAnchor > SWAP_DOCK.SCROLL_DOWN_PX) this.setDock('mini');
          // Measure "down" from the highest point since the last change, so jitter never adds up.
          else if (y < this.dockAnchor) this.dockAnchor = y;
        }
      }

      // The partial dock's content height now (handle, header row, tabs, buy row). The sheet always
      // shows at least that much (--sw-peek-content). The space kept for it under the stage
      // (--sw-peek-measured, first set before paint by stone-swap-sheet.liquid) follows only while
      // nothing has been painted yet or when the width changes: resizing the stage under the
      // shopper's eyes would be a layout shift.
      measurePeek({ widthChanged = false } = {}) {
        if (!this.mobile.matches) return;
        const grab = this.querySelector('.swap-sheet__grab');
        const parts = ['.swap-sheet__row', '.swap-sheet__tabs', '.swap-sheet__buy'].map((s) => this.querySelector(s));
        if (!grab || parts.some((el) => !el)) return;
        const height = Math.ceil(
          grab.offsetHeight + parseFloat(getComputedStyle(grab).marginTop) + parts.reduce((sum, el) => sum + el.offsetHeight, 0)
        );
        if (!height || height === this.peekContent) return;
        this.peekContent = height;
        (this.page || this).style.setProperty('--sw-peek-content', `${height}px`);
        const root = document.documentElement.style;
        if ((!this.swapPainted || widthChanged) && root.getPropertyValue('--sw-peek-measured') !== `${height}px`)
          root.setProperty('--sw-peek-measured', `${height}px`);
      }

      // The dock is position: fixed; bottom: 0 and sized by its content; no viewport arithmetic
      // positions it (Safari reports innerHeight, visualViewport and the vh units inconsistently
      // while it settles its toolbars, above all at load). The one exception: when the visual
      // viewport is proven shorter than the layout viewport (an on-screen keyboard), lift the dock
      // by the difference so it stays above it (--sw-keyboard-lift, negative). Never at load, never
      // positive, never while pinch-zoomed.
      measureViewport() {
        if (!this.mobile.matches || !this.loaded) return;
        const vv = window.visualViewport;
        if (!vv || Math.abs(vv.scale - 1) > 0.01) return;
        const hidden = window.innerHeight - (vv.offsetTop + vv.height);
        const shift = hidden > 1 ? `${-Math.round(hidden)}px` : '0px';
        if (shift !== this.keyboardLift) (this.page || this).style.setProperty('--sw-keyboard-lift', (this.keyboardLift = shift));
      }

      sync() {
        const part = this.activePart || 'stone';
        const open = !this.mobile.matches || this.dataset.d === 'half';
        this.dataset.part = part;
        this.zoomToPendant();
        // While the phone sheet is open the whole stage is one button: "Show full necklace".
        if (this.viewer) {
          const collapsible = this.sheetOpenOnPhone();
          if (collapsible) {
            this.viewer.setAttribute('role', 'button');
            this.viewer.setAttribute('aria-label', 'Show full necklace');
            this.viewer.removeAttribute('aria-roledescription');
          } else {
            this.viewer.setAttribute('role', 'region');
            this.viewer.setAttribute('aria-label', 'Product images');
            this.viewer.setAttribute('aria-roledescription', 'carousel');
          }
          this.area.classList.toggle('is-collapsible', collapsible);
        }
        this.querySelectorAll('.swap-tab').forEach((tab) =>
          tab.setAttribute('aria-expanded', String(open && tab.dataset.swapOpen === part && (!!this.activePart || !this.mobile.matches)))
        );
        this.querySelector('[data-swap-expand]')?.setAttribute('aria-expanded', String(this.sheetOpenOnPhone()));
      }

      // With the sheet half open on a phone, zoom the necklace onto its pendant so the stone being
      // swapped stays large in the space above the sheet. The renders share one framing (square,
      // client/stone-necklace/renders/QA_LOG.md): the pendant, bail loop to the bottom of the wrap,
      // spans 72%-92% of the height, centred left-right; the stone is ~9% of the width.
      zoomToPendant() {
        if (!this.area || !this.wrap) return;
        const zoom = this.mobile.matches && this.dataset.d === 'half';
        this.area.classList.toggle('is-zoomed', zoom);
        if (!zoom) return;
        this.goTo(0, false);
        const box = (this.viewer || this.wrap).getBoundingClientRect();
        const stage = this.stage || this.viewer;
        // The render is drawn object-fit: contain, so it is the largest centred square in the stage.
        const w = stage.offsetWidth;
        const h = stage.offsetHeight;
        // Phones also scale the image up a little about its centre to trim the white margin.
        const active = stage.querySelector('.swap-stage__img.is-active');
        const trim = (active && new DOMMatrixReadOnly(getComputedStyle(active).transform).a) || 1;
        const side = Math.min(w, h) * trim;
        // The expanded sheet is its full (untransformed) height, bottom-aligned (stone-swap.css).
        const sheetTop = window.innerHeight - this.offsetHeight;
        // While revealImage() scrolls the page, aim for where the viewer will end up.
        const room = Math.max(80, sheetTop - (box.top + (this.scrollShift || 0)));
        // Stone about 140px wide, the whole pendant within 80% of the room above the sheet.
        const k = Math.max(1, Math.min(3.2, 140 / (side * 0.09), (room * 0.8) / (side * 0.2)));
        const py = h / 2 + side * (0.82 - 0.5);
        this.area.style.setProperty('--sw-zoom', k);
        this.area.style.setProperty('--sw-zoom-x', `${(w / 2) * (1 - k)}px`);
        this.area.style.setProperty('--sw-zoom-y', `${room / 2 - py * k}px`);
      }

      // Where the page should scroll to after a swap so the necklace shows in full, or null when it
      // already does.
      // Phones: the page's very top. The stage is sized (stone-swap.css) to fill exactly the space
      // between the header and the partial dock there, so scrollY 0 is the settled view, the same
      // one the page loads in, whatever the header or toolbar heights.
      // Desktop: the image is sticky, so the page only moves when the image is actually off screen,
      // and then to the image less its scroll-margin-top (stone-swap.css).
      revealTarget() {
        if (!this.area) return null;
        if (this.mobile.matches) return window.scrollY > 0.5 ? 0 : null;
        const header = document.querySelector('.section-header');
        const sticky = !!header && getComputedStyle(header).position === 'sticky';
        // Scrolling up brings Dawn's on-scroll-up header back, so leave room for it.
        const headerHeight = sticky ? header.offsetHeight : 0;
        const rect = this.area.getBoundingClientRect();
        const view = (this.viewer || this.wrap).getBoundingClientRect();
        const margin = parseFloat(getComputedStyle(this.area).scrollMarginTop) || 0;
        const top =
          view.bottom + window.scrollY <= window.innerHeight
            ? 0
            : Math.max(0, Math.round(rect.top + window.scrollY - headerHeight - margin));
        const visibleTop = sticky ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const away = view.top < visibleTop - 2 || view.bottom > window.innerHeight + 2;
        return away ? top : null;
      }

      // After a swap, bring the necklace back into view if the shopper has scrolled away from it (for
      // example down to the description).
      revealImage(target = this.revealTarget()) {
        if (target === null) return;
        this.scrollShift = window.scrollY - target;
        this.sync();
        this.scrollPageTo(target);
      }

      // A programmatic scroll. The dock ignores it (it never counts as the shopper scrolling down),
      // and it ends exactly on the target: if the smooth scroll stops short (on iPhones a focus or
      // layout change can cut one off) it is finished in place. The shopper touching, wheeling or
      // using a scroll key hands the page straight back to them.
      scrollPageTo(target) {
        const run = (this.autoScroll = { target });
        window.scrollTo({ top: target, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        let last = window.scrollY;
        let still = 0;
        let frames = 0;
        let retried = false;
        const step = () => {
          if (this.autoScroll !== run) return;
          const y = window.scrollY;
          still = Math.abs(y - last) < 0.5 ? still + 1 : 0;
          last = y;
          const there = Math.abs(y - target) < 0.5;
          if (!there && !retried && still >= SWAP_DOCK.STALL_FRAMES) {
            retried = true;
            still = 0;
            window.scrollTo({ top: target, behavior: 'auto' });
          }
          const done = (there && still >= SWAP_DOCK.STILL_FRAMES) || (retried && still >= SWAP_DOCK.STALL_FRAMES);
          if (done || ++frames > SWAP_DOCK.MAX_FRAMES) this.endAutoScroll(run);
          else requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }

      // Ends the given programmatic scroll (or whichever is running, when the shopper takes over).
      endAutoScroll(run = this.autoScroll) {
        if (!run || this.autoScroll !== run) return;
        this.autoScroll = null;
        // Re-measure now the scroll has settled (the phone pendant zoom depends on the stage position).
        this.scrollShift = 0;
        this.dockAnchor = window.scrollY;
        this.sync();
        this.measureViewport();
        this.updateDock();
      }

      /* ---------- Slim bar (phones) ---------- */

      // Its Add to cart submits Dawn's own product form with Dawn's own button (so Dawn's cart
      // drawer, errors and analytics all run as usual) and mirrors that button's state.
      initMini() {
        const add = (this.miniAdd = this.querySelector('[data-swap-mini-add]'));
        const form = this.querySelector('product-form');
        const realButton = () => form?.querySelector('[type="submit"]');
        this.querySelector('[data-swap-expand]')?.addEventListener('click', () => this.expand());
        if (!add) return;
        if (!realButton()) {
          add.hidden = true;
          return;
        }
        add.addEventListener('click', () => {
          const button = realButton();
          if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          if (button.form?.requestSubmit) button.form.requestSubmit(button);
          else button.click();
        });
        const mirror = () => {
          const button = realButton();
          if (!button) return;
          const busy = button.classList.contains('loading') || button.getAttribute('aria-disabled') === 'true';
          add.disabled = button.disabled;
          add.classList.toggle('is-loading', busy && !button.disabled);
          if (busy) add.setAttribute('aria-disabled', 'true');
          else add.removeAttribute('aria-disabled');
          const label = button.querySelector('span')?.textContent.trim() || button.textContent.trim();
          if (label && add.textContent !== label) add.textContent = label;
        };
        mirror();
        this.miniObserver = new MutationObserver(mirror);
        this.miniObserver.observe(form, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
      }

      bindEvents() {
        // Window and document listeners go when the element does.
        this.listeners?.abort();
        this.listeners = new AbortController();
        const signal = this.listeners.signal;
        const passive = { passive: true, signal };

        // A tap/click on an option is a choice made: on phones the sheet closes so the whole necklace
        // shows. Arrow keys also fire change while browsing a group, so those keep the sheet open.
        this.addEventListener('pointerdown', (event) => {
          this.pickedAt = event.target.closest('variant-selects') ? event.timeStamp : 0;
        });
        ['pointerover', 'pointerdown', 'focusin'].forEach((type) => this.addEventListener(type, (event) => this.preloadIntent(event)));
        this.addEventListener('change', (event) => {
          if (!event.target.closest('variant-selects')) return;
          this.showSelection();
          this.render();
          const picked = event.timeStamp - (this.pickedAt || -Infinity) < SWAP_DOCK.PICK_WINDOW_MS;
          this.pickedAt = 0;
          const target = this.revealTarget();
          // Close to where the page will end: the partial dock at the top (on phones a swap always
          // brings the necklace back there), the slim bar if the page stays scrolled down.
          if (picked && this.sheetOpenOnPhone()) this.collapse((target ?? window.scrollY) <= SWAP_DOCK.TOP_PX ? 'peek' : 'mini');
          this.revealImage(target);
        });
        // Dawn re-renders the picker after the fetch; keep everything in sync with what it shows.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            this.swapImage(data?.variant?.featured_media);
            this.updateSlides(data?.variant);
            this.render({ announce: false });
          });
        }

        document.addEventListener(
          'click',
          (event) => {
            const opener = event.target.closest('[data-swap-open]');
            if (opener && (this.contains(opener) || this.area?.contains(opener))) this.open(opener.dataset.swapOpen);
          },
          { signal }
        );

        // Drag the handle / summary row: up opens, down closes, a tap on the handle toggles.
        [this.querySelector('.swap-sheet__grab'), this.querySelector('.swap-sheet__row')].forEach((handle) => {
          if (!handle) return;
          let startY = null;
          handle.addEventListener('pointerdown', (event) => {
            startY = event.clientY;
            handle.setPointerCapture(event.pointerId);
          });
          handle.addEventListener('pointerup', (event) => {
            if (startY === null || !this.mobile.matches) return;
            const dy = event.clientY - startY;
            startY = null;
            if (dy < -SWAP_DOCK.DRAG_PX) {
              this.activePart = this.activePart || this.lastPart || 'stone';
              this.setDock('half');
            } else if (dy > SWAP_DOCK.DRAG_PX) {
              this.setDock(this.restingState());
            } else if (handle.classList.contains('swap-sheet__grab')) {
              if (this.dataset.d === 'half') this.setDock(this.restingState());
              else this.open(this.activePart || this.lastPart || 'stone');
            }
          });
          handle.addEventListener('pointercancel', () => (startY = null));
        });

        // Escape closes the half sheet from anywhere on the page (the zoom viewer handles its own).
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && this.sheetOpenOnPhone() && !this.lightbox?.open) this.collapse();
          },
          { signal }
        );

        // Hide the sheet while Dawn's cart drawer or menu drawer is open. While Dawn's add-to-cart
        // notification shows (phones), drop the dock to the slim bar so its Add to cart can't be
        // mistaken for the notification's Check out, and put it back when the notification closes.
        // Coalesced to one check a frame, and only acts when what it looks for has changed (class
        // changes anywhere on the page land here, including the stage's own).
        let coverQueued = false;
        let lastCover = null;
        const checkCover = () => {
          coverQueued = false;
          const covered = this.mobile.matches && !!document.querySelector('cart-drawer.active, .menu-opening, details[open] > .menu-drawer');
          const notice = this.mobile.matches && !!document.querySelector('#cart-notification.active');
          const key = `${covered}|${notice}`;
          if (key === lastCover) return;
          lastCover = key;
          this.style.visibility = covered ? 'hidden' : '';
          if (notice && !this.beforeNotice) {
            this.beforeNotice = { d: this.dataset.d, part: this.activePart || this.lastPart };
            if (this.dataset.d !== 'mini') this.setDock('mini');
          } else if (!notice && this.beforeNotice) {
            const { d, part } = this.beforeNotice;
            this.beforeNotice = null;
            if (d === 'half') this.open(part || 'stone');
            else this.setDock(this.restingState());
          }
        };
        this.coverObserver = new MutationObserver((records) => {
          // Our own class changes (stage, sheet) can't open a drawer: no frame for them.
          if (coverQueued || records.every((r) => this.contains(r.target) || this.area?.contains(r.target))) return;
          coverQueued = true;
          requestAnimationFrame(checkCover);
        });
        this.coverObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class', 'open'] });

        // How much the page grows between dock checks (late content, scroll anchoring).
        this.pageObserver = new ResizeObserver(([entry]) => {
          const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
          if (this.pageHeight !== undefined) this.pageGrowth = (this.pageGrowth || 0) + height - this.pageHeight;
          this.pageHeight = height;
        });
        this.pageObserver.observe(document.body);

        // Phone dock: page scrolls (at most one check a frame, no layout reads but scrollY).
        let scrollQueued = false;
        window.addEventListener(
          'scroll',
          () => {
            if (scrollQueued) return;
            scrollQueued = true;
            requestAnimationFrame(() => {
              scrollQueued = false;
              this.updateDock();
            });
          },
          passive
        );
        // A finger (or mouse button) on the dock holds its state until shortly after it lifts. Touch
        // uses touch events: a touch that turns into a page scroll cancels its pointer, not its touch.
        const press = () => {
          this.fingerDown = true;
          clearTimeout(this.graceTimer);
        };
        const lift = () => {
          if (!this.fingerDown) return;
          this.fingerDown = false;
          this.fingerUpAt = performance.now();
          clearTimeout(this.graceTimer);
          this.graceTimer = setTimeout(() => this.updateDock(), SWAP_DOCK.TAP_GRACE_MS);
        };
        this.addEventListener('touchstart', press, { passive: true });
        this.addEventListener('pointerdown', (event) => event.pointerType !== 'touch' && press());
        window.addEventListener('touchend', (event) => !event.touches.length && lift(), passive);
        window.addEventListener('touchcancel', (event) => !event.touches.length && lift(), passive);
        ['pointerup', 'pointercancel'].forEach((type) =>
          window.addEventListener(type, (event) => event.pointerType !== 'touch' && lift(), passive)
        );
        // The shopper taking over the page ends our programmatic scroll at once.
        const takeOver = () => this.endAutoScroll();
        window.addEventListener('pointerdown', takeOver, passive);
        window.addEventListener('wheel', takeOver, passive);
        window.addEventListener(
          'keydown',
          (event) => ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(event.key) && takeOver(),
          passive
        );

        this.mobile.addEventListener('change', () => this.setDock(this.mobile.matches ? this.restingState() : 'peek'), { signal });
        // Resizes (rotation, iOS toolbars): re-measure once a frame, re-fit names only on a new width.
        let resizeQueued = false;
        let lastWidth = window.innerWidth;
        window.addEventListener(
          'resize',
          () => {
            if (resizeQueued) return;
            resizeQueued = true;
            requestAnimationFrame(() => {
              resizeQueued = false;
              this.measureViewport();
              this.sync();
              if (window.innerWidth !== lastWidth) {
                lastWidth = window.innerWidth;
                this.fitTabs();
                this.measurePeek({ widthChanged: true });
              }
            });
          },
          passive
        );
        // iOS toolbars and Safari's layouts move the visual viewport without always resizing the window.
        let viewportQueued = false;
        const onViewport = () => {
          if (viewportQueued) return;
          viewportQueued = true;
          requestAnimationFrame(() => {
            viewportQueued = false;
            this.measureViewport();
          });
        };
        window.visualViewport?.addEventListener('resize', onViewport, passive);
        window.visualViewport?.addEventListener('scroll', onViewport, passive);
        window.addEventListener('pageshow', onViewport, passive);
        // The web font changes text widths when it arrives.
        document.fonts?.ready.then(() => {
          this.fitTabs();
          this.measurePeek();
        });
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.coverObserver?.disconnect();
        this.miniObserver?.disconnect();
        this.pageObserver?.disconnect();
        this.unsubscribe?.();
        clearTimeout(this.graceTimer);
        clearTimeout(this.restTimer);
        this.autoScroll = null;
      }

    }
  );
}
